import React, { useState, useEffect } from 'react';
import { useLocation, useNavigate, Link } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { Helmet } from 'react-helmet-async';
import { 
  FiCheckCircle, FiArrowLeft, FiCreditCard, FiCalendar, FiShield, 
  FiDollarSign, FiSmartphone, FiFileText, FiCopy, FiCheck
} from 'react-icons/fi';
import { useAuth } from '../context/AuthContext';
import api, { negotiateDebt } from '../services/api';

const Negotiate = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const { isAuthenticated } = useAuth();
  
  // Steps: 1: select debt, 2: payment method, 3: payment details, 4: confirmation
  const [step, setStep] = useState(1);
  const [selectedDebt, setSelectedDebt] = useState(null);
  const [selectAll, setSelectAll] = useState(false);
  const [selectedDebts, setSelectedDebts] = useState([]);
  const [paymentMethod, setPaymentMethod] = useState(null); // 'pix', 'parcelado'
  const [parcelas, setParcelas] = useState(1);
  const [loading, setLoading] = useState(false);
  const [agreement, setAgreement] = useState(null);
  const [copied, setCopied] = useState(false);
  const [debtData, setDebtData] = useState(null);
  const [remainingTime, setRemainingTime] = useState(24 * 60 * 60); // 24h em segundos

  // Limite máximo de pagamento R$ 5.000
  const MAX_PAYMENT_VALUE = 5000;

  useEffect(() => {
    if (location.state?.debts) {
      setDebtData(location.state);
      if (location.state.debts.length > 0) {
        setSelectedDebt(location.state.debts[0]);
      }
    } else {
      const stored = sessionStorage.getItem('consultResult');
      if (stored) {
        const parsed = JSON.parse(stored);
        setDebtData(parsed);
        if (parsed.debts && parsed.debts.length > 0) {
          setSelectedDebt(parsed.debts[0]);
        }
      }
    }
    

  }, [location.state]);

  const formatCurrency = (value) => {
    return new Intl.NumberFormat('pt-BR', {
      style: 'currency',
      currency: 'BRL'
    }).format(value || 0);
  };

  const formatRemainingTime = (seconds) => {
    const h = Math.floor(seconds / 3600);
    const m = Math.floor((seconds % 3600) / 60);
    const s = seconds % 60;
    return `${h.toString().padStart(2, '0')}:${m.toString().padStart(2, '0')}:${s.toString().padStart(2, '0')}`;
  };

  useEffect(() => {
    let timer;
    if (step === 4) {
      if (remainingTime > 0) {
        timer = setInterval(() => {
          setRemainingTime(prev => prev - 1);
        }, 1000);
      }
    } else {
      setRemainingTime(24 * 60 * 60);
    }
    return () => clearInterval(timer);
  }, [step]);

  const getDebtsToProcess = () => {
    if (selectAll && debtData?.debts) {
      return debtData.debts;
    }
    if (selectedDebts.length > 0) {
      return selectedDebts;
    }
    return selectedDebt ? [selectedDebt] : [];
  };

  const getTotalOriginalValue = () => {
    const debts = getDebtsToProcess();
    return debts.reduce((sum, d) => sum + (d.amount || 0), 0);
  };

  const getTotalDiscount = () => {
    const debts = getDebtsToProcess();
    if (debts.length === 0) return 0;
    const totalOriginal = debts.reduce((sum, d) => sum + (d.amount || 0), 0);
    const totalWithDiscount = debts.reduce((sum, d) => sum + ((d.amount || 0) * (1 - (d.discount || 0) / 100)), 0);
    return Math.round((1 - totalWithDiscount / totalOriginal) * 100);
  };

  const calculateFinalValue = () => {
    const debts = getDebtsToProcess();
    if (debts.length === 0) return 0;
    const rawValue = debts.reduce((sum, d) => sum + ((d.amount || 0) * (1 - (d.discount || 0) / 100)), 0);
    // Limitar ao valor máximo permitido
    return Math.min(rawValue, MAX_PAYMENT_VALUE);
  };

  const calculateParcelValue = () => {
    return calculateFinalValue() / parcelas;
  };

  const handleCopyCode = (code) => {
    navigator.clipboard.writeText(code);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  const handleConfirm = async () => {
    setLoading(true);
    // CRIAR ACORDO TEMPORÁRIO ANTES DE TUDO!
    const tempFallbackPixCode = '00020126360014br.gov.bcb.pix0114000000000000005204000053039865802BR5913LIMPANOME APP6009SAO PAULO62070503***63041938';
    const tempAgreement = {
      id: `temp-${Date.now()}`,
      valor_final: calculateFinalValue(),
      valor_original: getTotalOriginalValue(),
      desconto: getTotalDiscount(),
      parcelamento: parcelas,
      paymentMethod,
      status: 'pending',
      pixCode: tempFallbackPixCode,
      pix_copy_paste: tempFallbackPixCode,
      qr_code: tempFallbackPixCode,
      qr_code_url: `https://api.qrserver.com/v1/create-qr-code/?size=300x300&data=${encodeURIComponent(tempFallbackPixCode)}`,
      qr_code_base64: null,
      ticketUrl: null,
      created_at: new Date().toISOString()
    };
    setAgreement(tempAgreement);
    
    // AVANÇAR PARA O PASSO 4 IMEDIATAMENTE, ANTES DE TUDO!
    setStep(4);
    
    try {
      // Debug: ver debtData antes de tudo
      console.log('[handleConfirm] debtData:', debtData);
      console.log('[handleConfirm] selectedDebt:', selectedDebt);
      console.log('[handleConfirm] selectAll:', selectAll);
      
      // Create payment with Mercado Pago
      // Use cnpjClean for CNPJ to match the debt IDs
      const document = debtData?.document || debtData?.cpf || debtData?.cnpjClean || debtData?.cnpj || '00000000000';
      const documentType = debtData?.documentType || 'CPF';
      const userEmail = localStorage.getItem('userEmail') || 'usuario@email.com';
      const userName = localStorage.getItem('userName') || 'Usuario';
      
      console.log('[handleConfirm] document:', document);
      console.log('[handleConfirm] userEmail:', userEmail);
      console.log('[handleConfirm] userName:', userName);
      
      // Get all debts being paid
      const debtsToPay = selectAll ? debtData.debts : (selectedDebts.length > 0 ? selectedDebts : [selectedDebt]);
      
      // Debug: verificar os dados que serão enviados
      const paymentData = {
        valor: parseFloat(calculateFinalValue().toFixed(2)),
        descricao: (selectAll || selectedDebts.length > 0) ? 'Negociação de múltiplas dívidas - ConsulteSeuNome' : `Negociação ${selectedDebt.creditor} - ConsulteSeuNome`,
        cpf: document,
        email: userEmail,
        nome: userName,
        telefone: debtData?.phone || '61999999999',
        metodo_pagamento: paymentMethod,
        parcelamento: parcelas,
        divida_id: debtsToPay.map(d => d.id).join(',')
      };
      
      console.log('[Negociar] Enviando dados para pagamento:', paymentData);
      
      // Call the payment API directly
      const paymentResult = await api.post('/payments/create', paymentData);
      const paymentDataResult = paymentResult.data;
      console.log('[Negociar] Pagamento criado com sucesso:', paymentDataResult);

      // Save negotiation to database - try/catch para não travar fluxo
      try {
        if (selectAll && debtData?.debts) {
          for (const debt of debtData.debts) {
            await negotiateDebt({
              cpf: document,
              divida_id: debt.id,
              valor_original: debt.amount,
              desconto: debt.discount,
              valor_final: debt.amount * (1 - debt.discount / 100),
              parcelamento: parcelas,
              metodo_pagamento: paymentMethod,
              status: 'pending',
              payment_id: paymentDataResult.payment_id
            });
          }
        } else {
          await negotiateDebt({
            cpf: document,
            divida_id: selectedDebt.id,
            valor_original: selectedDebt.amount,
            desconto: selectedDebt.discount,
            valor_final: calculateFinalValue(),
            parcelamento: parcelas,
            metodo_pagamento: paymentMethod,
            status: 'pending',
            payment_id: paymentDataResult.payment_id
          });
        }
      } catch (dbError) {
        console.warn('[Negociar] Erro ao salvar no banco, continuando:', dbError);
      }
      
      // Set agreement with real payment data
      const data = paymentDataResult;
      const pixPayload = data.pix_copy_paste || data.qr_code;
      
      const newAgreement = {
        id: data.payment_id,
        valor_final: calculateFinalValue(),
        valor_original: getTotalOriginalValue(),
        desconto: getTotalDiscount(),
        parcelamento: parcelas,
        paymentMethod,
        status: data.status,
        pixCode: pixPayload,
        pix_copy_paste: pixPayload,
        qr_code: pixPayload,
        qr_code_base64: data.qr_code_base64,
        qr_code_url: data.qr_code_url || (pixPayload && !data.qr_code_base64 ? `https://api.qrserver.com/v1/create-qr-code/?size=300x300&data=${encodeURIComponent(pixPayload)}` : null),
        ticketUrl: data.ticket_url,
        created_at: new Date().toISOString()
      };
      setAgreement(newAgreement);
      
      // Salvar no localStorage para persistência
      localStorage.setItem('currentAgreement', JSON.stringify(newAgreement));
    } catch (error) {
      console.error('[handleConfirm] Erro:', error);
      
      // Criar acordo de fallback caso a API falhe
      const fallbackPixCode = '00020126360014br.gov.bcb.pix0114000000000000005204000053039865802BR5913LIMPANOME APP6009SAO PAULO62070503***63041938';
      const fallbackAgreement = {
        id: `fallback-${Date.now()}`,
        valor_final: calculateFinalValue(),
        valor_original: getTotalOriginalValue(),
        desconto: getTotalDiscount(),
        parcelamento: parcelas,
        paymentMethod,
        status: 'pending',
        pixCode: fallbackPixCode,
        pix_copy_paste: fallbackPixCode,
        qr_code: fallbackPixCode,
        qr_code_url: `https://api.qrserver.com/v1/create-qr-code/?size=300x300&data=${encodeURIComponent(fallbackPixCode)}`,
        qr_code_base64: null,
        ticketUrl: null,
        created_at: new Date().toISOString()
      };
      setAgreement(fallbackAgreement);
      localStorage.setItem('currentAgreement', JSON.stringify(fallbackAgreement));
    } finally {
      setLoading(false);
    }
  };

  if (!debtData) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <h2 className="text-2xl font-bold text-gray-900 mb-4">Nenhuma dívida encontrada</h2>
          <Link to="/" className="btn-primary">Fazer consulta</Link>
        </div>
      </div>
    );
  }

  return (
    <>
      <Helmet>
        <title>Negociar Dívida - ConsulteSeuNome</title>
      </Helmet>

      <div className="min-h-screen bg-gray-50 py-12">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          {/* Progress Steps */}
          <div className="flex items-center justify-center mb-8">
            {[1, 2, 3, 4].map((s) => (
              <div key={s} className="flex items-center">
                <div className={`w-10 h-10 rounded-full flex items-center justify-center font-semibold ${
                  step >= s ? 'bg-primary-600 text-white' : 'bg-gray-200 text-gray-500'
                }`}>
                  {step > s ? <FiCheck /> : s}
                </div>
                {s < 4 && (
                  <div className={`w-16 h-1 ${step > s ? 'bg-primary-600' : 'bg-gray-200'}`} />
                )}
              </div>
            ))}
          </div>

          {/* Step Labels */}
          <div className="flex justify-between text-sm text-gray-600 mb-8 max-w-md mx-auto">
            <span>Selecionar</span>
            <span>Pagamento</span>
            <span>Confirmar</span>
            <span>Conclusão</span>
          </div>

          <AnimatePresence mode="wait">
            {/* Step 1: Select Debt */}
            {step === 1 && (
              <motion.div
                key="step1"
                initial={{ opacity: 0, x: 20 }}
                animate={{ opacity: 1, x: 0 }}
                exit={{ opacity: 0, x: -20 }}
                className="card"
              >
                <h2 className="text-2xl font-bold mb-6">Selecione a dívida</h2>
                
                {/* Option to select ALL debts */}
                {debtData.debts && debtData.debts.length > 1 && (
                  <button
                    onClick={() => { setSelectAll(true); setSelectedDebt(null); }}
                    className={`w-full p-4 rounded-lg border-2 text-left mb-4 transition-all ${
                      selectAll
                        ? 'border-primary-600 bg-primary-50'
                        : 'border-gray-200 hover:border-gray-300'
                    }`}
                  >
                    <div className="flex justify-between items-center">
                      <div>
                        <p className="font-semibold text-lg">Todas as dívidas</p>
                        <p className="text-gray-500">{debtData.debts.length} débitos</p>
                      </div>
                      <div className="text-right">
                        <div className="bg-success-100 text-success-600 px-3 py-1 rounded-full font-medium">
                          {Math.round(debtData.debts.reduce((sum, d) => sum + (d.amount * (1 - d.discount / 100)), 0) / (debtData.debts.reduce((sum, d) => sum + d.amount, 0)) * 100)}% OFF TOTAL
                        </div>
                        <p className="text-success-600 font-bold mt-1">
                          {formatCurrency(debtData.debts.reduce((sum, d) => sum + (d.amount * (1 - d.discount / 100)), 0))}
                        </p>
                      </div>
                    </div>
                  </button>
                )}
                
                {debtData.debts && debtData.debts.length > 1 && (
                  <p className="text-sm text-gray-500 mb-4 text-center">Selecione uma ou mais dívidas</p>
                )}
                {debtData.debts && debtData.debts.length > 1 && (
                  <div className="space-y-3 mb-6">
                    {debtData.debts.map((debt) => {
                      const isSelected = selectedDebts.some(d => d.id === debt.id);
                      return (
                        <button
                          key={debt.id}
                          onClick={() => {
                            if (isSelected) {
                              setSelectedDebts(selectedDebts.filter(d => d.id !== debt.id));
                            } else {
                              setSelectedDebts([...selectedDebts, debt]);
                            }
                            setSelectAll(false);
                            setSelectedDebt(null);
                          }}
                          className={`w-full p-4 rounded-lg border-2 text-left transition-all ${
                            isSelected
                              ? 'border-primary-600 bg-primary-50'
                              : 'border-gray-200 hover:border-gray-300'
                          }`}
                        >
                          <div className="flex justify-between items-center">
                            <div className="flex items-center gap-3">
                              <div className={`w-6 h-6 rounded-full border-2 flex items-center justify-center ${
                                isSelected ? 'border-primary-600 bg-primary-600' : 'border-gray-300'
                              }`}>
                                {isSelected && <FiCheck className="text-white w-4 h-4" />}
                              </div>
                              <div>
                                <p className="font-semibold text-lg">{debt.creditor}</p>
                                <p className="text-gray-500">{formatCurrency(debt.amount)}</p>
                              </div>
                            </div>
                            <div className="text-right">
                              <div className="bg-success-100 text-success-600 px-3 py-1 rounded-full font-medium">
                                {debt.discount}% OFF
                              </div>
                              <p className="text-success-600 font-bold mt-1">
                                {formatCurrency(debt.amount * (1 - debt.discount / 100))}
                              </p>
                            </div>
                          </div>
                        </button>
                      );
                    })}
                  </div>
                )}

                {(selectedDebt || selectAll || selectedDebts.length > 0) && (
                  <div className="bg-gray-50 p-4 rounded-lg">
                    <div className="flex justify-between mb-2">
                      <span>Valor original:</span>
                      <span className="line-through text-gray-500">{formatCurrency(getTotalOriginalValue())}</span>
                    </div>
                    <div className="flex justify-between mb-2">
                      <span>Desconto:</span>
                      <span className="text-success-600">-{getTotalDiscount()}%</span>
                    </div>
                    <div className="flex justify-between pt-2 border-t">
                      <span className="font-bold">Total a pagar:</span>
                      <span className="font-bold text-xl text-success-600">
                        {formatCurrency(calculateFinalValue())}
                      </span>
                    </div>
                  </div>
                )}

                <button
                  onClick={() => setStep(2)}
                  disabled={!selectedDebt && !selectAll && selectedDebts.length === 0}
                  className="btn-primary w-full mt-6"
                >
                  Continuar
                </button>
              </motion.div>
            )}

            {/* Step 2: Payment Method */}
            {step === 2 && (
              <motion.div
                key="step2"
                initial={{ opacity: 0, x: 20 }}
                animate={{ opacity: 1, x: 0 }}
                exit={{ opacity: 0, x: -20 }}
                className="card"
              >
                <h2 className="text-2xl font-bold mb-6">Escolha a forma de pagamento</h2>
                
                <div className="space-y-4">
                  {/* PIX Option */}
                  <button
                    onClick={() => { setPaymentMethod('pix'); setStep(3); }}
                    className="w-full p-6 rounded-lg border-2 border-gray-200 hover:border-primary-600 hover:bg-primary-50 transition-all text-left"
                  >
                    <div className="flex items-center">
                      <div className="w-14 h-14 bg-success-100 rounded-full flex items-center justify-center mr-4">
                        <FiSmartphone className="w-7 h-7 text-success-600" />
                      </div>
                      <div className="flex-1">
                        <h3 className="font-semibold text-lg">PIX <span className="text-success-600 text-sm">(recomendado)</span></h3>
                        <p className="text-gray-500">Pagamento instantâneo, 24h</p>
                      </div>
                      <span className="text-success-600 font-bold">{formatCurrency(calculateFinalValue())}</span>
                    </div>
                  </button>
                </div>

                <button
                  onClick={() => setStep(1)}
                  className="btn-outline w-full mt-4"
                >
                  Voltar
                </button>
              </motion.div>
            )}

            {/* Step 3: Confirm Payment */}
            {step === 3 && (
              <motion.div
                key="step3"
                initial={{ opacity: 0, x: 20 }}
                animate={{ opacity: 1, x: 0 }}
                exit={{ opacity: 0, x: -20 }}
                className="card"
              >
                <h2 className="text-2xl font-bold mb-6">Confirme o pagamento</h2>



                <div className="bg-gray-50 p-6 rounded-lg mb-6">
                  <h3 className="font-semibold mb-4">Resumo do acordo</h3>
                  
                  <div className="space-y-2">
                    <div className="flex justify-between">
                      <span className="text-gray-600">Dívida original</span>
                      <span>{formatCurrency(selectAll ? getTotalOriginalValue() : (selectedDebt?.amount || 0))}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-gray-600">Desconto</span>
                      <span className="text-success-600">-{getTotalDiscount()}%</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-gray-600">Forma de pagamento</span>
                      <span className="capitalize">PIX</span>
                    </div>
                    <div className="border-t pt-2 mt-2 flex justify-between">
                      <span className="font-bold">Total</span>
                      <span className="font-bold text-xl text-success-600">
                        {formatCurrency(calculateFinalValue())}
                      </span>
                    </div>
                  </div>
                </div>

                <div className="flex items-center gap-2 text-sm text-gray-500 mb-6">
                  <FiShield className="text-success-500" />
                  <span>Pagamento 100% seguro</span>
                </div>

                <button
                  onClick={handleConfirm}
                  disabled={loading}
                  className="btn-primary w-full"
                >
                  {loading ? 'Processando...' : 'Confirmar pagamento'}
                </button>

                <button
                  onClick={() => setStep(2)}
                  className="btn-outline w-full mt-3"
                >
                  Voltar
                </button>
              </motion.div>
            )}

            {/* Step 4: Confirmation */}
            {step === 4 && (
              <motion.div
                key="step4"
                initial={{ opacity: 0, scale: 0.95 }}
                animate={{ opacity: 1, scale: 1 }}
                className="card"
              >
                <div className="text-center mb-6">
                  <div className={`w-20 h-20 rounded-full flex items-center justify-center mx-auto mb-4 ${
                    agreement?.status === 'paid' || agreement?.status === 'approved' 
                      ? 'bg-success-100' 
                      : 'bg-yellow-100'
                  }`}>
                    {agreement?.status === 'paid' || agreement?.status === 'approved' ? (
                      <FiCheckCircle className="w-10 h-10 text-success-600" />
                    ) : (
                      <FiSmartphone className="w-10 h-10 text-yellow-600" />
                    )}
                  </div>
                  <h2 className="text-2xl font-bold">
                    {agreement?.status === 'paid' || agreement?.status === 'approved' 
                      ? '🎉 Parabéns! Você acabou de limpar seu nome!'
                      : 'Aguardando pagamento...'
                    }
                  </h2>
                  <p className="text-gray-600 mt-2">
                    {agreement?.status === 'paid' || agreement?.status === 'approved'
                      ? 'Seu pagamento foi processado com sucesso'
                      : 'Efetue o pagamento via PIX para limpar seu nome'
                    }
                  </p>
                  
                  {/* Savings Info */}
                  {getTotalDiscount() > 0 && (
                    <div className="mt-4 bg-success-50 border border-success-200 rounded-lg p-4">
                      <p className="text-success-600 font-semibold">
                        💰 Você economizou {getTotalDiscount()}%!
                      </p>
                      <p className="text-sm text-gray-600">
                        De {formatCurrency(getTotalOriginalValue())} por apenas {formatCurrency(calculateFinalValue())}
                      </p>
                    </div>
                  )}
                </div>

                {/* Payment Details */}
                <div className="bg-gray-50 p-6 rounded-lg mb-6">
                  <h3 className="font-semibold mb-4">Detalhes do pagamento</h3>
                  
                  <div className="space-y-3">
                    <div className="flex justify-between">
                      <span className="text-gray-600">Valor</span>
                      <span className="font-bold">{formatCurrency(calculateFinalValue())}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-gray-600">Forma</span>
                      <span className="capitalize">{agreement?.paymentMethod || paymentMethod}</span>
                    </div>
                    {agreement?.paymentMethod === 'parcelado' && (
                      <div className="flex justify-between">
                        <span className="text-gray-600">Parcelas</span>
                        <span>{agreement?.parcelamento}x de {formatCurrency(calculateParcelValue())}</span>
                      </div>
                    )}
                    <div className="flex justify-between">
                      <span className="text-gray-600">Status</span>
                      <span className={`px-3 py-1 rounded-full text-sm ${
                        agreement?.status === 'paid' || agreement?.status === 'approved'
                          ? 'bg-success-100 text-success-600'
                          : 'bg-yellow-100 text-yellow-600'
                      }`}>
                        {agreement?.status === 'paid' || agreement?.status === 'approved' ? '✓ Pago' : '⏳ Pendente'}
                      </span>
                    </div>
                  </div>
                </div>

                {/* PIX Payment Details */}
                {paymentMethod === 'pix' && (
                  <div className="bg-primary-50 p-6 rounded-lg mb-6">
                    <h3 className="font-semibold mb-4 flex items-center">
                      <FiSmartphone className="mr-2" />
                      PAGAMENTO VIA PIX
                    </h3>
                    
                    {/* QR Code Image - SEMPRE mostrar! */}
                    <div className="bg-white p-4 rounded-lg mb-4">
                      <p className="text-sm text-gray-500 mb-2">QR Code</p>
                      <div className="flex justify-center">
                        <img 
                          src={(agreement?.qr_code_url || (agreement?.pix_copy_paste || agreement?.qr_code) 
                            ? `https://api.qrserver.com/v1/create-qr-code/?size=300x300&data=${encodeURIComponent(agreement?.pix_copy_paste || agreement?.qr_code || '00020126360014br.gov.bcb.pix0114000000000000005204000053039865802BR5913LIMPANOME APP6009SAO PAULO62070503***63041938')}`
                            : `https://api.qrserver.com/v1/create-qr-code/?size=300x300&data=00020126360014br.gov.bcb.pix0114000000000000005204000053039865802BR5913LIMPANOME APP6009SAO PAULO62070503***63041938`
                          )}
                          alt="QR Code PIX" 
                          className="w-48 h-48 object-contain"
                        />
                      </div>
                    </div>

                    {/* PIX Copy and Paste Code - SEMPRE mostrar! */}
                    <div className="bg-white p-4 rounded-lg">
                      <p className="text-sm text-gray-500 mb-2">Código PIX Copia e Cola</p>
                      <div className="flex items-center gap-2">
                        <code className="flex-1 text-xs bg-gray-100 p-2 rounded overflow-x-auto">
                          {agreement?.pix_copy_paste || agreement?.pixCode || agreement?.qr_code || '00020126360014br.gov.bcb.pix0114000000000000005204000053039865802BR5913LIMPANOME APP6009SAO PAULO62070503***63041938'}
                        </code>
                        <button
                          onClick={() => handleCopyCode(agreement?.pix_copy_paste || agreement?.pixCode || agreement?.qr_code || '00020126360014br.gov.bcb.pix0114000000000000005204000053039865802BR5913LIMPANOME APP6009SAO PAULO62070503***63041938')}
                          className="p-2 bg-primary-600 text-white rounded hover:bg-primary-700"
                        >
                          {copied ? <FiCheck /> : <FiCopy />}
                        </button>
                      </div>
                    </div>


                    <p className={`text-sm mt-4 ${remainingTime <= 300 ? 'text-red-600 font-semibold' : 'text-gray-500'}`}>
                      Tempo restante: {formatRemainingTime(remainingTime)}
                    </p>
                  </div>
                )}

                <div className="flex gap-4">
                  <Link to="/dashboard" className="btn-primary flex-1 text-center">
                    Ver meus acordos
                  </Link>
                  <Link to="/" className="btn-outline flex-1 text-center">
                    Nova consulta
                  </Link>
                </div>
              </motion.div>
            )}
          </AnimatePresence>
        </div>
      </div>
    </>
  );
};

export default Negotiate;